// The Stage's programme (zones/events/zone.json, docs/design/briefs/events.md): what's on tonight, this week and this
// season, each item with its art, its Plaza venue and a time in the viewer's zone. Pure: `now` and the time zone are
// always passed, so tests and the screenshot harness get the same page.
//
// Two sources, merged:
// - the zone's dated `moments` (the season's fixed nights and dates), with the art and venue this module adds per id;
// - rooms' calendar (`GET /v1/events?world=plaza`, src/events.ts), when it answers. A rooms event at the time of a
//   fixed night is the same night: it lends the night its plaza instance and live status, and isn't listed twice.
// The calendar is never empty: with nothing ahead, the page shows the Plaza, which is always open.

import { dayKey, KIND_LABEL, type PlazaEvent } from "../../events.ts";
import { dateMs, mediaUrl, type Moment } from "../spec.ts";

export type VenueId = "podium" | "tholos" | "terraces";
export type ItemKind = "night" | "season" | "deadline" | "pitch" | "concert" | "meetup";

export interface Venue {
  id: VenueId;
  /** as players read it: "the temple podium" (w12-plaza's names, plaza-athenaion.md §5) */
  name: string;
  what: string;
  line: string;
  art: string;
}

/**
 * Which world the Plaza runs (games/plaza src/world.js). The venues keep their wire ids in every world (owner decision
 * 2026-10-20); their names, lines and art are the world's. The classical kit's world east is the Plaza from the switch
 * (owner, 2026-10-21, before the 31 Oct night; docs/build/world-builder/PHASE8.md 8f); its art is in
 * zones/events/classical/ (games/plaza/tools/venue-renders.js --world east). Athenaion is retired (owner, 2026-10-22).
 */
export type PlazaWorld = "classical";
export const PLAZA_WORLD: PlazaWorld = "classical";

/** The classical world's venues, in world east (its baked anchors: games/plaza tools/classical-east.js). */
const CLASSICAL_VENUES: Record<VenueId, Venue> = {
  podium: {
    id: "podium",
    name: "the altar steps",
    what: "The events stage",
    line: "Showcases, concerts and the big nights, at the altar in the east court with the Parthenon's east front behind. The crowd gathers in the court.",
    art: "/zones/events/classical/venue-podium.jpg",
  },
  tholos: {
    id: "tholos",
    name: "the exedra",
    what: "Pitch nights",
    line: "A half-round stone bench by the temple's north-west corner. Makers pitch what they made to the people sitting in its rows.",
    art: "/zones/events/classical/venue-tholos.jpg",
  },
  terraces: {
    id: "terraces",
    name: "the meeting circles",
    what: "Breakout rooms",
    line: "Circles of seats at the landing, the watchtower, the summit and the coast shrine, for talking with the people around you after the show.",
    art: "/zones/events/classical/venue-terraces.jpg",
  },
};

export const VENUES_BY_WORLD: Record<PlazaWorld, Record<VenueId, Venue>> = { classical: CLASSICAL_VENUES };
/** The Plaza's venues, in the world it runs now. */
export const VENUES: Record<VenueId, Venue> = VENUES_BY_WORLD[PLAZA_WORLD];

/** The Plaza card's one line, per world (src/plaza.ts). */
export const PLAZA_BLURB: Record<PlazaWorld, string> = {
  classical: "The Parthenon on a plateau between two rivers. Meet people, catch a show at the altar, walk the paths out to the town, the harbour and the hills, and step through the portals into games.",
};

export interface Item {
  id: string;
  kind: ItemKind;
  title: string;
  line: string;
  /** unix ms */
  at: number;
  /** unix ms; when it's over (a deadline is over at `at`) */
  until: number;
  venue: VenueId | null;
  /** a served image path: every item has art */
  art: string;
  /** where the card leads (an anchor on this page, or the zone that holds it) */
  href: string;
  live: boolean;
  /** the plaza instance, when rooms holds it (Join in Banger) */
  instance?: string;
  /** the rooms event behind it, when there is one (RSVP goes to rooms then) */
  eventId?: string;
  /** how many are going, from rooms */
  rsvps?: number;
  /** a moment of this zone (countdown:<id>) or a rooms event */
  source: "zone" | "rooms";
}

/**
 * What the spec doesn't carry for each fixed moment: its kind, its line and its title once it's on. Venue, art and
 * href live on the moments in zones/events/zone.json (spec `moments[].venue/art/href`); a test keeps the ids in step.
 */
export const MOMENT_META: Record<string, { kind: ItemKind; line: string; onTitle?: string }> = {
  "haunt-opens": {
    kind: "season",
    line: "Spooky games from creators everywhere, free to play. Visit three haunts and light up your pumpkin.",
    onTitle: "Open Haunt is on",
  },
  "ship-by": {
    kind: "deadline",
    line: "Make a haunt with the free template and pack. Ship it before the deadline and it can be played on stage.",
  },
  "plaza-night": {
    kind: "night",
    line: "The week's haunts, played live on stage. Come as a pumpkin.",
  },
};

/**
 * The art's other framings, rendered from the Plaza itself (games/plaza/tools/venue-renders.js): `card` (960×600) for
 * small cards, `phone` (750×600, the 375 px hero at 2×) for phones. Art without them uses itself.
 */
export function artVariants(art: string): { card: string; phone: string } {
  const m = /^(\/zones\/events\/(?:classical\/)?(?:night|plaza|venue-(?:podium|tholos|terraces)))\.jpg$/.exec(art);
  return m ? { card: `${m[1]}-card.jpg`, phone: `${m[1]}-phone.jpg` } : { card: art, phone: art };
}

/** On now (a night or a rooms event, not a season or a deadline): its "I'm going" turns into Join (wave 13). */
export const joinsNow = (i: Pick<Item, "live" | "kind">): boolean => i.live && i.kind !== "season" && i.kind !== "deadline";

const isVenue = (v: unknown): v is VenueId => v === "podium" || v === "tholos" || v === "terraces";

/**
 * Seasons and deadlines are calendar dates, not times: they read in UTC (the season ends Tue 10 Nov, the deadline is
 * the end of Fri 6 Nov) wherever the viewer is. Nights and rooms events read in the viewer's zone.
 */
export const dateZone = (i: Pick<Item, "kind">, timeZone: string): string => (i.kind === "season" || i.kind === "deadline" ? "UTC" : timeZone);

const HOUR = 3_600_000;
const DAY = 86_400_000;

/** Which venue a rooms event uses (plaza-athenaion.md "Which venue"): big nights on the podium, pitch nights at the tholos, meetups on the terraces. */
export function venueFor(e: Pick<PlazaEvent, "kind" | "featured" | "showcase"> & { venue?: string }): VenueId {
  if (isVenue(e.venue)) return e.venue; // rooms' own (the host's pick, else the same rule)
  if (e.kind === "meetup") return "terraces";
  if (e.kind === "concert" || e.featured || e.showcase) return "podium";
  return "tholos";
}

/** The zone's moments as programme items (those this module knows), dropping what's over. */
export function momentItems(moments: readonly Moment[], now: number): Item[] {
  const out: Item[] = [];
  for (const m of moments) {
    const meta = MOMENT_META[m.id];
    const at = dateMs(m.at);
    if (!meta || Number.isNaN(at)) continue;
    const until = dateMs(m.until) || (meta.kind === "deadline" ? at : at + 2 * HOUR);
    if (until <= now) continue;
    const venue = isVenue(m.venue) ? m.venue : null;
    const art = mediaUrl(m.art) ?? (venue ? VENUES[venue].art : "/zones/events/hero.svg");
    out.push({ id: m.id, kind: meta.kind, title: at <= now && meta.onTitle ? meta.onTitle : m.label, line: meta.line, at, until, venue, art, href: m.href ?? `#on-${m.id}`, live: at <= now && meta.kind !== "season", source: "zone" });
  }
  return out;
}

const ROOMS_LINE: Record<PlazaEvent["kind"], string> = {
  pitch: "Makers take the stage one at a time and pitch what they made. Play it now, or follow them.",
  concert: "Live music in the Plaza. Performers come up one after another.",
  meetup: "Circles of seats to sit and talk with the people around you.",
};

/** A rooms calendar entry as a programme item. */
export function roomsItem(e: PlazaEvent, now: number): Item {
  const venue = venueFor(e);
  const until = e.endsAt ?? e.at + 2 * HOUR;
  return {
    id: e.id,
    kind: e.kind,
    title: e.title || KIND_LABEL[e.kind],
    line: ROOMS_LINE[e.kind],
    at: e.status === "live" && e.startedAt ? e.startedAt : e.at,
    until: Math.max(until, now + (e.status === "live" ? HOUR : 0)),
    venue,
    art: VENUES[venue].art,
    href: `#${e.id}`,
    live: e.status === "live",
    instance: e.instance,
    eventId: e.id,
    rsvps: e.rsvps,
    source: "rooms",
  };
}

/** Both sources, soonest first (live ones first). A rooms event within an hour of a fixed night is that night. */
export function programme(moments: readonly Moment[], rooms: readonly PlazaEvent[], now: number): Item[] {
  const fixed = momentItems(moments, now);
  const extra: Item[] = [];
  for (const e of rooms) {
    const night = fixed.find((f) => f.kind === "night" && Math.abs(f.at - e.at) < HOUR);
    if (night) {
      night.instance = e.instance;
      night.eventId = e.id;
      night.rsvps = e.rsvps;
      night.live ||= e.status === "live";
      continue;
    }
    const it = roomsItem(e, now);
    if (it.until > now) extra.push(it);
  }
  return [...fixed, ...extra].sort((a, b) => Number(b.live) - Number(a.live) || a.at - b.at || a.id.localeCompare(b.id));
}

const STAGE_KINDS: ItemKind[] = ["night", "pitch", "concert", "meetup"];

/** The "next up" hero: what's live, else the next night on a stage, else the next anything; null when nothing is ahead. */
export function nextUp(items: readonly Item[], now: number): Item | null {
  const live = items.find((i) => i.live);
  if (live) return live;
  const ahead = items.filter((i) => i.at > now);
  return ahead.find((i) => STAGE_KINDS.includes(i.kind)) ?? ahead[0] ?? null;
}

export type BandId = "tonight" | "week" | "season";
export interface Band {
  id: BandId;
  title: string;
  items: Item[];
}

/**
 * Calendar bands, so one date never splits: Tonight (live, or on today's date), This week (today and the next six
 * calendar days, in the viewer's zone), and the season (later dates, plus running seasons such as Open Haunt). An
 * item's date is read in its own zone (dateZone). Empty bands are left out.
 */
export function bands(items: readonly Item[], now: number, timeZone: string): Band[] {
  const today = dayKey(now, timeZone);
  const lastOfWeek = dayKey(now + 6 * DAY, timeZone);
  const b: Record<BandId, Item[]> = { tonight: [], week: [], season: [] };
  for (const i of items) {
    const day = dayKey(i.at, dateZone(i, timeZone));
    if (i.kind === "season") b.season.push(i);
    else if (i.live || day === today) b.tonight.push(i);
    else if (day <= lastOfWeek) b.week.push(i);
    else b.season.push(i);
  }
  const titles: Record<BandId, string> = { tonight: "Tonight", week: "This week", season: "This season" };
  return (["tonight", "week", "season"] as BandId[]).filter((id) => b[id].length).map((id) => ({ id, title: titles[id], items: b[id] }));
}

// ---- words and time ------------------------------------------------------------------------------------------

/** An item's date line: "Sat 31 Oct at 22:00 EET" (viewer's zone), "Fri 30 Oct, end of day (UTC)", "23 Oct – 2 Nov". */
export function itemWhen(i: Pick<Item, "at" | "until" | "kind">, timeZone: string): string {
  const f = (at: number, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("en-GB", { ...o, timeZone: "UTC" }).format(at);
  if (i.kind === "deadline") return `${f(i.at, { weekday: "short", day: "numeric", month: "short" })}, end of day (UTC)`;
  if (i.kind === "season") return `${f(i.at, { day: "numeric", month: "short" })} – ${f(i.until - 1, { day: "numeric", month: "short" })}`;
  return whenText(i.at, timeZone);
}

/** "Sat 31 Oct at 20:00 GMT" in the viewer's zone, with the zone's short name. */
export function whenText(at: number, timeZone: string): string {
  const day = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone }).format(at);
  const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone, timeZoneName: "short" }).format(at);
  return `${day} at ${time}`;
}

/** The programme's left-hand time column: "20:00" over "Sat 31 Oct". */
export function timeColumn(i: Pick<Item, "at" | "kind" | "live" | "until">, viewerZone: string): { big: string; small: string } {
  const timeZone = dateZone(i, viewerZone);
  const day = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone }).format(i.at);
  const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone }).format(i.at);
  if (i.live) return { big: "Live", small: "now" };
  if (i.kind === "season") {
    const end = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone }).format(i.until - 1);
    return { big: day.replace(/^\w+ /, ""), small: `to ${end}` };
  }
  if (i.kind === "deadline") return { big: day.replace(/^\w+ /, ""), small: "end of day" };
  return { big: time, small: day };
}

/** "13d 07h 12m", or "07h 12m 09s" under a day; "" once it has passed. */
export function countdownText(target: number, now: number): string {
  const ms = target - now;
  if (ms <= 0) return "";
  const t = Math.floor(ms / 1000);
  const [d, h, m, s] = [Math.floor(t / 86400), Math.floor((t % 86400) / 3600), Math.floor((t % 3600) / 60), t % 60];
  const p = (n: number) => String(n).padStart(2, "0");
  return d > 0 ? `${d}d ${p(h)}h ${p(m)}m` : `${p(h)}h ${p(m)}m ${p(s)}s`;
}

/** The status line next to an item's countdown. */
export function clockLine(i: Pick<Item, "at" | "until" | "kind" | "live">, now: number): { label: string; target: number | null; live: boolean } {
  if (i.kind === "season") return now < i.at ? { label: "Opens in", target: i.at, live: false } : { label: "On now · ends in", target: i.until, live: true };
  if (i.live || (i.at <= now && now < i.until)) return { label: i.kind === "deadline" ? "Closing" : "Live now · ends in", target: i.until, live: true };
  if (now >= i.until) return { label: "Over", target: null, live: false };
  return { label: i.kind === "deadline" ? "Closes in" : "Starts in", target: i.at, live: false };
}
